/**
 * @author Nihar <nihar.baishyar@314ecorp.com>
 * @description utils for payer
 */

import _ from 'lodash';
import { TRANSACTION_TYPE_CODES } from 'app-constants/admin/payer';
import { schemaKeys } from 'components/forms/schema/eligibilityRequestFormSchema';

const DEFAULT_PROVIDER_IDENTIFIER_COMBINATION = [[schemaKeys.npi]];

const NOT_SUPPORTED_TRANSACTION_FILTER = {
	value: 'notSupported',
	label: 'Not Supported',
	color: '#595959',
};

const defaultTransactionMeta: Payers.ITransactionMeta = {
	isEligibility: false,
	isClaimStatus: false,
	isEnrollmentRequiredForEligibility: false,
	isEnrollmentRequiredForClaimStatus: false,
};

const getTransactionMeta = (transaction: Payers.ITransaction): Payers.ITransactionMeta => {
	const transactionMeta = _.cloneDeep(defaultTransactionMeta);
	_.map(transaction, ({ transactionType, enrollmentRequired }) => {
		if (transactionType === TRANSACTION_TYPE_CODES.eligibility) {
			transactionMeta.isEligibility = true;
			transactionMeta.isEnrollmentRequiredForEligibility = enrollmentRequired || false;
		} else if (transactionType === TRANSACTION_TYPE_CODES.claimStatus) {
			transactionMeta.isClaimStatus = true;
			transactionMeta.isEnrollmentRequiredForClaimStatus = enrollmentRequired || false;
		}
	});
	return transactionMeta;
};

const prepareSelectedPayersMeta = (data: Payers.ISelectedPayerDetails[]): Payers.ISelectedPayer[] => {
	return _.map(data, (selectedPayer) => {
		const transactionMeta = getTransactionMeta(selectedPayer.transactions);
		return { ...selectedPayer, ...transactionMeta };
	});
};

const prepareAllPayersMeta = (data: Payers.IRegisteredPayerDetails[]): Payers.IRegisteredPayer[] => {
	return _.map(data, (registeredPayer) => {
		const transactionMeta = getTransactionMeta(registeredPayer.transactions);
		return { ...registeredPayer, ...transactionMeta };
	});
};

const preparePayerMeta = (data: Payers.IPayerDetails): Payers.IPayer => {
	const transactionMeta = getTransactionMeta(data.transactions);

	const eligibilityMeta = _.get(data.transactions, TRANSACTION_TYPE_CODES.eligibility) as Payers.IEligibilityMeta;
	if (!_.isEmpty(eligibilityMeta)) {
		eligibilityMeta.requiredFieldCombinations = {
			patient: eligibilityMeta?.requiredFields?.patient || [],
			provider: eligibilityMeta?.supportedProviderIdentifiers || DEFAULT_PROVIDER_IDENTIFIER_COMBINATION,
			encounter: eligibilityMeta?.requiredFields?.encounter || [],
		};
	}
	const claimStatusMeta = _.get(data.transactions, TRANSACTION_TYPE_CODES.claimStatus) as Payers.IClaimStatusMeta;
	if (!_.isEmpty(claimStatusMeta)) {
		claimStatusMeta.requiredFieldCombinations = {
			patient: claimStatusMeta?.requiredFields?.patient || [],
			provider: claimStatusMeta?.supportedProviderIdentifiers || DEFAULT_PROVIDER_IDENTIFIER_COMBINATION,
			Claim: claimStatusMeta?.requiredFields?.Claim || [],
			encounter: claimStatusMeta?.requiredFields?.encounter || [],
		};
	}
	const memberIdMeta = _.get(data.transactions, TRANSACTION_TYPE_CODES.memberId) as Payers.IMemberIdMeta;
	if (!_.isEmpty(memberIdMeta)) {
		memberIdMeta.requiredFieldCombinations = memberIdMeta.requiredFields?.patient || [];
	}
	return {
		...data,
		...transactionMeta,
		eligibilityMeta,
		claimStatusMeta,
		memberIdMeta,
	};
};

/**
 * Groups maintenance data by transaction type, combining markdown content
 */
const groupTransactionMaintenance = (
	maintenanceData: Payers.IPayerMaintenance[],
): Record<Payers.ITransactionType, Payers.IMaintenanceInfo> => {
	const transactionInfo = {} as Record<Payers.ITransactionType, Payers.IMaintenanceInfo>;

	_.forEach(maintenanceData, ({ transactions, timings, isDown = false }) => {
		const markdown = timings?.markdown;
		if (markdown) {
			_.forEach(transactions, (transactionId) => {
				const existingMeta = _.get(transactionInfo, [transactionId]) || ({} as Payers.IMaintenanceInfo);
				if (!existingMeta) {
					transactionInfo[transactionId] = { markdown, isDown };
				}
				//
				else {
					transactionInfo[transactionId] = {
						markdown: existingMeta.markdown ? existingMeta.markdown + markdown : markdown,
						isDown: existingMeta.isDown || isDown,
					};
				}
			});
		}
	});

	return transactionInfo;
};

type TenantPayerTableRow = import('@veritable/common/adminApi').TenantPayer & {
	mappedPayer?: Payers.IRegisteredPayer;
	selectedPayer?: Payers.ISelectedPayer;
};

/**
 * Filters tenant payers based on filter parameters
 * Handles both mapped and unmapped payers with search across source and Veritable payer names
 */
const getFilteredPayers = (tenantPayers: TenantPayerTableRow[], filterParams: Payers.IFilterParams): TenantPayerTableRow[] => {
	let filteredPayers = tenantPayers;

	// Filter by name - search in both source name and Veritable payer name
	if (filterParams.name) {
		const searchText = filterParams.name.toLowerCase();
		filteredPayers = _.filter(filteredPayers, (payer) => {
			const sourceName = _.toLower(payer.name || '');
			const veritablePayerName = _.toLower(payer.payerName || payer.mappedPayer?.name || '');
			return sourceName.includes(searchText) || veritablePayerName.includes(searchText);
		});
	}

	// Filter by enrollment required
	if (!_.isEmpty(filterParams.enrollmentRequired)) {
		const enrollmentRequiredValues = filterParams.enrollmentRequired;
		// If both/none are selected, don't filter
		if (enrollmentRequiredValues.length !== 2) {
			filteredPayers = _.filter(filteredPayers, (payer) => {
				// Only filter mapped payers
				if (!payer.mappedPayer) {
					return false;
				}
				return _.includes(enrollmentRequiredValues, payer.mappedPayer.enrollmentRequired);
			});
		}
	}

	// Filter by status
	if (!_.isEmpty(filterParams.status)) {
		const statusValues = filterParams.status;
		filteredPayers = _.filter(filteredPayers, (payer) => {
			const status = payer.selectedPayer?.status;
			if (_.isNumber(status)) {
				return _.includes(statusValues, status);
			}
			return false;
		});
	}

	// Filter by transactions
	if (!_.isEmpty(filterParams.transactions)) {
		const transactionsValues = filterParams.transactions;
		const includesNotSupported = _.includes(transactionsValues, NOT_SUPPORTED_TRANSACTION_FILTER.value);
		filteredPayers = _.filter(filteredPayers, (payer) => {
			if (!payer.mappedPayer) {
				return includesNotSupported;
			}
			return _.some(payer.mappedPayer.transactions, (transaction) => _.includes(transactionsValues, transaction.transactionType));
		});
	}

	return filteredPayers;
};

export {
	DEFAULT_PROVIDER_IDENTIFIER_COMBINATION,
	NOT_SUPPORTED_TRANSACTION_FILTER,
	prepareSelectedPayersMeta,
	prepareAllPayersMeta,
	preparePayerMeta,
	groupTransactionMaintenance,
	getFilteredPayers,
};
